import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';
import ApplyResetWrapper from '@/components/filters/ApplyResetWrapper';
import AmenitiesFilter from '@/components/filters/AmenitiesFilter';
import type { Filter, MeasurementUnit } from '@/models/Filter';
import type { FilterOptionsAndLabels } from '@/utils/filters/options/getFilterOptions';
import SpaceDetailsFilter from '@/components/filters/SpaceDetailsFilter';
import VenueTypeFilter from '@/components/filters/VenueTypeFilter';
import { IndustryRatingFilter } from '@/components/filters/IndustryRatingFilter';
import VenueDetailsFilter from '@/components/filters/VenueDetailsFilter';
import {
  applicableFilterApplied,
  getAllFiltersAppliedCount,
  restaurantVenueTypeClicked,
  shouldVisible
} from '@/utils/filters/filterHelper';
import { FILTERS_TO_RESET } from '@/constants/filterConstants';
import LocationDetailsFilter from '@/components/filters/LocationDetailsFilter';
import type { Locale } from '@/i18n';
import type { BrandAndChainResponse } from '@/app/api/venue-autocomplete/route';
import { type DropdownOptionShape } from '@cvent/carina/components/Dropdown';
import AllFiltersSortBy from '@/components/filters/AllFiltersSortBy';
import dynamic from 'next/dynamic';
import type { FeatureConfig } from '@/fetchers/getFeatureConfigs';
import { formatMessage } from '@/utils/translationHelper';

const RestaurantRatingContainer = dynamic(() => import('@/components/filters/RestaurantRatingContainer'), {
  ssr: false
});
const BrandAndChainFilter = dynamic(() => import('@/components/filters/BrandAndChainFilter'), { ssr: false });
const AccountDetailsFilter = dynamic(() => import('@/components/filters/AccountDetailsFilter'), { ssr: false });

type Props = {
  searchTerm: string;
  measurementUnit: MeasurementUnit;
  geo: string | undefined;
  locale: Locale;
  options: FilterOptionsAndLabels;
  applicableFilters: Array<string>;
  appliedFilters: Filter;
  isFiltersPanelOpen: boolean;
  setIsFiltersPanelOpen: (isOpen: boolean) => void;
  locationRadiusMessage: string;
  isLoggedIn: boolean;
  sortOptions: { label: string; value: string }[];
  setSortingType: Dispatch<SetStateAction<string>>;
  setSortingLabel: Dispatch<SetStateAction<string>>;
  sortType: string;
  setSort: Dispatch<SetStateAction<string>>;
  sort: string;
  setShowShadow: (value: boolean) => void;
  featureConfig: FeatureConfig | null;
  setIsRfpBannerVisible: (isRfpBannerVisible: boolean) => void;
  aiMode: boolean;
  isSearchExpanded: boolean;
  setIsSearchExpanded: (v: boolean) => void;
};

export default function AllFilters({
  searchTerm,
  measurementUnit,
  geo,
  locale,
  options,
  applicableFilters,
  appliedFilters,
  setIsFiltersPanelOpen,
  isFiltersPanelOpen,
  locationRadiusMessage,
  isLoggedIn,
  sortOptions,
  setSortingType,
  setSortingLabel,
  sortType,
  sort,
  setSort,
  setShowShadow,
  featureConfig,
  setIsRfpBannerVisible,
  aiMode,
  isSearchExpanded,
  setIsSearchExpanded
}: Props): JSX.Element {
  const [selected, setSelected] = useState(appliedFilters);

  useEffect(() => {
    setSelected(appliedFilters);
    setBrandCityCountry(null);
  }, [appliedFilters]);

  const {
    venueTypeOptions,
    accountTagsOptions,
    industryRatings,
    amenities,
    venueDetailsOptions,
    spaceMeasurementOptions,
    spaceCapacityOptions,
    roomLayoutOptions,
    roomQuantitiesOptions,
    rateHistoryLabels,
    restaurantPriceRangeOptions,
    localizedLabels,
    locationDetailsOptions,
    errorLabels
  } = options;

  const {
    maximumRadius,
    spaceDetails,
    airportDistance,
    maxMinError,
    inputNumberErrorForZero,
    wholeNumberRequired,
    howMuchSpace,
    calculateCapacity,
    capacityCalculatorTitle,
    capacityCalculatorRoomLayoutLabel,
    capacityCalculatorRearProjectionRequiredLabel,
    capacityCalculatorSocialDistancingLabel,
    capacityCalculatorAttendeesPerDay,
    capacityCalculatorSqFeetLabel,
    capacityCalculatorSqMetreLabel,
    capacityCalculatorFeetLabel,
    capacityCalculatorClickingApplyMsg,
    capacityCalculatorApplyLabel,
    capacityCalculatorCloseLabel,
    allFilters,
    allFiltersWithCount,
    venueType,
    accountTagsTitle,
    venueDetailsTitle,
    venueDetailsInfoIconAriaLabel,
    accountDetailsTitle,
    cancelledSpaceTitle,
    cancelledSpaceLabel,
    cancelledSpaceFlyout,
    lessTag,
    moreTag,
    selectAll,
    clear,
    resetAllFilters,
    cancelAllFilters,
    applyAllFilters,
    apply,
    restaurantPriceRangeTitle,
    locationDetails,
    close,
    back
  } = localizedLabels;

  const [brandCityCountry, setBrandCityCountry] = useState<BrandAndChainResponse | null>(null);

  const spaceMeasurementsCategory = applicableFilterApplied(spaceMeasurementOptions, applicableFilters, selected);
  const spaceCapacityCategory = applicableFilterApplied(spaceCapacityOptions, applicableFilters, selected);
  const roomQuantitiesCategory = applicableFilterApplied(roomQuantitiesOptions, applicableFilters, selected);
  const spaceDetailsCategory = { spaceMeasurementsCategory, spaceCapacityCategory, roomQuantitiesCategory };

  // all filters count to be shown only in AI mode
  const filterCount = useMemo(() => {
    if (aiMode) {
      return getAllFiltersAppliedCount(selected);
    }
    return 0;
  }, [aiMode, selected]);

  return (
    <ApplyResetWrapper
      setIsSearchExpanded={setIsSearchExpanded}
      isSearchExpanded={isSearchExpanded}
      title={aiMode ? formatMessage(allFiltersWithCount, locale, { count: filterCount }) : allFilters}
      filterCount={aiMode ? filterCount : 0}
      aiMode={aiMode}
      venueAutoComplete={{ searchTerm, measurementUnit, geo, locale, brandCityCountry, setBrandCityCountry }}
      selected={selected}
      setSelected={setSelected}
      appliedFilters={appliedFilters}
      filters={FILTERS_TO_RESET}
      isOpen={isFiltersPanelOpen}
      setIsOpen={setIsFiltersPanelOpen}
      testID="af"
      panel
      strings={{ allFilters, resetAllFilters, cancelAllFilters, applyAllFilters, clear, apply }}
      showShadow
      setSortingLabel={setSortingLabel}
      setSortingType={setSortingType}
      sortOptions={sortOptions}
      sortType={sortType}
      sort={sort}
      setSort={setSort}
      searchTerm={searchTerm}
      setIsRfpBannerVisible={setIsRfpBannerVisible}
    >
      {isFiltersPanelOpen ? (
        <>
          <AllFiltersSortBy
            sortOptions={sortOptions as DropdownOptionShape[]}
            sortAriaLabel={options.localizedLabels.sort}
            sort={sort}
            setSort={setSort}
          />
          {(shouldVisible(['PLANNER_TAGS'], applicableFilters, selected) || isLoggedIn) && (
            <AccountDetailsFilter
              labels={{
                accountDetailsTitle,
                accountTagsTitle,
                cancelledSpaceTitle,
                lessTag,
                moreTag,
                cancelledSpaceLabel,
                cancelledSpaceFlyout,
                rateHistoryLabels
              }}
              accountTagsOptions={accountTagsOptions}
              selected={selected}
              setSelected={setSelected}
              appliedFilters={appliedFilters}
              locale={locale}
              applicableFilters={applicableFilters}
              isLoggedIn={isLoggedIn}
              featureConfig={featureConfig}
              testID="af-account-details"
            />
          )}
          <VenueTypeFilter
            options={venueTypeOptions}
            selected={selected}
            setSelected={setSelected}
            labels={{
              title: formatMessage(venueType, locale, { count: 0 }),
              selectAll,
              clear
            }}
            showTitle
            setShowShadow={setShowShadow}
            testID="af-venue-type"
          />
          {shouldVisible(['RATINGS'], applicableFilters, selected) && (
            <IndustryRatingFilter ratings={industryRatings} selected={selected} setSelected={setSelected} />
          )}
          {brandCityCountry && (
            <BrandAndChainFilter
              options={brandCityCountry.brandAndChainOptions}
              selected={selected}
              setSelected={setSelected}
              showTitle
              testID="af-brand-chain"
            />
          )}
          <VenueDetailsFilter
            title={venueDetailsTitle}
            ariaLabel={venueDetailsInfoIconAriaLabel}
            options={venueDetailsOptions}
            selected={selected}
            setSelected={setSelected}
            testID="af-venue-details"
          />
          {Boolean(
            roomQuantitiesCategory.length || spaceMeasurementsCategory.length || spaceCapacityCategory.length
          ) && (
            <SpaceDetailsFilter
              spaceDetailsCategory={spaceDetailsCategory}
              locale={locale}
              selected={selected}
              setSelected={setSelected}
              labels={{
                title: spaceDetails,
                maxMinError,
                wholeNumberRequired,
                howMuchSpace,
                calculateCapacity,
                capacityCalculatorTitle,
                capacityCalculatorRoomLayoutLabel,
                capacityCalculatorRearProjectionRequiredLabel,
                capacityCalculatorSocialDistancingLabel,
                capacityCalculatorAttendeesPerDay,
                capacityCalculatorSqFeetLabel,
                capacityCalculatorSqMetreLabel,
                capacityCalculatorFeetLabel,
                capacityCalculatorClickingApplyMsg,
                capacityCalculatorApplyLabel,
                capacityCalculatorCloseLabel,
                close,
                back
              }}
              spaceMeasurementsOption={spaceMeasurementOptions}
              spaceCapacityOption={spaceCapacityOptions}
              roomQuantitiesOption={roomQuantitiesOptions}
              roomLayoutOptions={roomLayoutOptions}
              measurementUnit={measurementUnit}
              errorLabels={errorLabels}
              testID="af-space-details"
            />
          )}
          <LocationDetailsFilter
            labels={{
              title: locationDetails,
              selectAll,
              clear
            }}
            brandCityCountry={brandCityCountry}
            options={locationDetailsOptions}
            selected={selected}
            setSelected={setSelected}
            applicableFilters={applicableFilters}
            inputNumberError={inputNumberErrorForZero}
            text={{ airportDistance, maximumRadius, locationRadiusMessage }}
            testID="af-location-details"
          />
          <AmenitiesFilter
            options={amenities.amenityOptions}
            selected={selected}
            setSelected={setSelected}
            labels={{
              title: amenities.amenitiesTitle,
              selectAll,
              clear
            }}
            testID="af-amenities"
          />
          {restaurantVenueTypeClicked(selected) && shouldVisible(['PRICE_CATEGORY'], applicableFilters, selected) && (
            <RestaurantRatingContainer
              title={restaurantPriceRangeTitle}
              options={restaurantPriceRangeOptions}
              selected={selected}
              setSelected={setSelected}
              testID="af-restaurant-rating"
            />
          )}
        </>
      ) : (
        <></>
      )}
    </ApplyResetWrapper>
  );
}
